import { COLOR_LIGHT } from "../../constants"
import { TreeGridTranslations } from "../hooks/use-translations"
import { makeCfgBase } from "../base/layout/make-cfg-base"
import { ToolbarBuilder } from "../base/layout/make-toolbar"
import { makeColumns } from "./columns"
import { makeFiltersRow } from "./rows/filters-row"
import { makePageHeaderRow } from "./rows/page-header-row"
import { makeTotalHoursRow } from "./rows/total-hours-row"
import { ApprovedHoursVisibleColumnName } from "./types"
import type { HourReportingWorkflowSettings } from "../../hour-reports/hooks/use-hour-reporting-workflow-settings"

export const makeLayout = (props: MakeLayoutProps) => {
  const Cfg = {
    ...makeCfgBase({ id: props.id, cfgId: props.cfgId }),
    MainCol: "date",
    NoTreeLines: 1,
    SaveAttrs: "Toolbar",
    Version: 1,
  }
  const Cols = makeColumns({
    columnOrder: props.columnOrder,
    visibleColumns: props.visibleColumns,
    workflowSettings: props.workflowSettings,
    labels: props.labels,
  })
  const Head = makeHead(props)
  const Header = makeHeader({ columns: Cols, titleRowLabel: props.labels.titleRow })
  const Lang = makeLang(props)
  const Toolbar = makeToolbar(props)
  const Actions = makeActions()
  const Solid = [{ id: "NoData", Html: props.noDataText }]

  return {
    Cfg,
    Cols,
    Head,
    Header,
    Lang,
    Toolbar,
    Actions,
    Solid,
    Def: makeDef(),
  }
}

const makeDef = () => {
  return [
    {
      Name: "DateGroup",
      Calculated: "1",
      Expanded: "1",
      Height: "48",
      Background: COLOR_LIGHT,
      dateClass: "group-header",
      projectOrTaskClass: "group-header",
      CanPrint: 2,
    },
    {
      Name: "ApprovedHour",
    },
  ]
}

const makeHead = ({ dateFormat, gridInfo, showFilters, isMemberSubContext, labels }: MakeHeadProps) => {
  const pageHeaderRow = makePageHeaderRow({ dateFormat, gridInfo, name: labels.headerRow })
  const filtersRow = makeFiltersRow({ showFilters })
  const totalHoursRow = makeTotalHoursRow({
    isMemberContext: isMemberSubContext,
    label: labels.totalHours,
    name: labels.totalHoursRow,
  })

  return [pageHeaderRow, filtersRow, totalHoursRow]
}

const makeHeader = ({ columns, titleRowLabel }: MakeHeaderProps) => {
  const Header: Record<string, string | number> = {
    Name: titleRowLabel,
    Wrap: 1,
    dateClass: "text-right",
  }

  columns.forEach((col) => {
    if (col.Name) Header[col.Name] = col.header as string
  })

  return Header
}

const makeLang = ({ firstDayOfWeek, dateSeparator, translations }: MakeLangProps) => {
  return {
    Format: {
      FirstWeekDay: firstDayOfWeek,
      GMT: 1,
      DateSeparator: dateSeparator,
      LongDayNames: translations.longDayNames,
      ShortDayNames: translations.shortDayNames,
      Day2CharNames: translations.day2CharNames,
      Day1CharNames: translations.day1CharNames,
      LongMonthNames: translations.longMonthNames,
      LongMonthNames2: translations.longMonthNames,
      ShortMonthNames: translations.shortMonthNames,
    },
    MenuColumns: {
      Caption: translations.chooseItemsLabel,
      ColsCaption: translations.chooseColumnsLabel,
      SpaceCaption: translations.chooseToolbarsLabel,
      RowsCaption: translations.chooseRowsLabel,
    },
    MenuButtons: {
      Ok: translations.okButtonLabel,
      Cancel: translations.cancelButtonLabel,
      HideAll: translations.hideAllButtonLabel,
      All: translations.allOnButtonLabel,
      Clear: translations.clear,
    },
    MenuPrint: {
      Caption: translations.printOptionsLabel,
      PrintOptions: translations.printOptionsLabel,
      PrintRows: translations.printMaxRowsPerPageLabel,
      PrintPageRoot: translations.printStartRowLabel,
      PrintPageSize: translations.printPageSizeLabel,
      PrintPageOptions: translations.printBrowserPrintInfoLabel,
      PrintPageOrientation: translations.printPageOrientationLabel,
      PrintPageOrientations: `${translations.portraitLabel},${translations.landscapeLabel}`,
      PrintMarginWidth: translations.printMarginWidthLabel,
      PrintMarginHeight: translations.printMarginHeightLabel,
      PDFFitPage: translations.printFitToPageLabel,
      PDFFitPages: `${translations.noneLabel},${translations.widthLabel},${translations.heightLabel},${translations.singlePageLabel}`,
      PrintDPI: translations.printDPIRatioLabel,
      PrintWidth: translations.printPageWidthLabel,
      PrintHeight: translations.printPageHeightLabel,
      PrintResults: translations.printSizeInfo,
    },
    MenuExport: {
      Caption: translations.exportOptionsLabel,
      ExportOptions: translations.exportOptionsLabel,
      ExportName: translations.exportFileNameLabel,
      ExportFormat: translations.exportFileFormatLabel,
      ExportExpanded: translations.exportAllRowsExpandedLabel,
      ExportOutline: translations.exportTreeInExcelLabel,
      ExportIndent: translations.exportIndentTreeInMainColumnLabel,
      ExportFixedRows: translations.exportFreezeHeadRowsLabel,
      ExportFixedCols: translations.exportFreezeLeftColumnsLabel,
      ExportEmpty: translations.exportEmptyCellsLabel,
    },
    MenuFilter: {
      F0: translations.filterOffLabel,
      F1: translations.filterEqualLabel,
      F2: translations.filterNotEqualLabel,
      F3: translations.filterLessThanLabel,
      F4: translations.filterLessThanOrEqualLabel,
      F5: translations.filterGreaterThanLabel,
      F6: translations.filterGreaterThanOrEqualLabel,
      F7: translations.filterBeginsWithLabel,
      F8: translations.filterDoesnotBeginWithLabel,
      F9: translations.filterEndsWithLabel,
      F10: translations.filterDoesnotEndWithLabel,
      F11: translations.filterContainsLabel,
      F12: translations.filterDoesnotContainLabel,
    },
  }
}

const makeToolbar = ({ showFilters, translations }: MakeToolbarProps) => {
  return new ToolbarBuilder()
    .addCustomToolbarConfig({
      Cells10Search: "Search",
      Scales: 0,
      ScalesLabel: "",
    })
    .addToolbarSearch(translations)
    .addToolbarFilter(showFilters)
    .addToolbarTips(translations)
    .build()
}

const makeActions = () => {
  return {
    OnIns: "",
    OnDel: "",
  }
}

type MakeLayoutProps = {
  id: string
  cfgId: string
  dateFormat: string
  dateSeparator: string
  firstDayOfWeek: DayOfWeek
  gridInfo: string
  noDataText: string
  showFilters: boolean
  translations: TreeGridTranslations
  columnOrder: ApprovedHoursVisibleColumnName[]
  visibleColumns: ApprovedHoursVisibleColumnName[]
  isMemberSubContext: boolean
  workflowSettings?: HourReportingWorkflowSettings
  labels: import("./columns").ApprovedHoursColumnLabels & ApprovedHoursRowLabels & { totalHours: string }
}

type MakeHeadProps = {
  dateFormat: string
  gridInfo: string
  showFilters: boolean
  isMemberSubContext: boolean
  labels: { totalHours: string } & ApprovedHoursRowLabels
}

type MakeHeaderProps = {
  columns: { header?: string; Name?: string }[]
  titleRowLabel: string
}

type ApprovedHoursRowLabels = {
  headerRow: string
  titleRow: string
  totalHoursRow: string
}

type MakeLangProps = {
  firstDayOfWeek: DayOfWeek
  dateSeparator: string
  translations: TreeGridTranslations
}

type MakeToolbarProps = {
  showFilters: boolean
  translations: TreeGridTranslations
}
